import moment from 'moment';
import React, {
  ChangeEvent,
  useState,
  FunctionComponent,
  useEffect,
  useMemo,
} from 'react';
import styled from 'styled-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useQueryClient } from 'react-query';

import {
  Select,
  Stack,
  Switch,
  Tooltip,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { formatDateDB } from '@fortress-technology-solutions/fortress-component-library/utils';
import { StateSelect } from './StateSelect';
import { MinimumSetAside } from './MinimumSetAside';
import ElementWithPermissions from '../../../components/ElementWithPermissions';
import { Input } from './Input';
import { AddressInput } from './AddressInput';
import { CalendarDatePicker } from './CalendarDatePicker';

import useIsAffordableProperty from '../../../hooks/useIsAffordableProperty';
import usePropertyHasProgram from '../../../hooks/usePropertyHasProgram';
import { AFFORDABLE_PROGRAMS } from '../../../utils/affordable';
import { formatCalendarDateUtc } from '../../../utils/date-helpers';

import type { Building, State } from '../types';

import appMessages from '../../App/messages';
import messages from '../messages';
import { formatNaurPercentage, validateApplicableFractionValue } from './utils';
import { useNaurEventsForBuilding } from '../../../hooks/data-fetching/useNaurEvents';

type Props = {
  building: Building,
  updateBuilding: Function,
  setSelectedBuilding: Function,
  disabled: boolean,
  states: State[],
  selectedProperty: Object,
  promptToaster: Function,
  intl: Object,
  showFundingType: boolean,
};

// prettier-ignore
const StyledSwitch = styled(Switch)`
  & > .MuiSwitch-track {
    &:before {
      left: -9px!important;
    }
    &:after {
      left: 8px!important;
    }
  }
`;

const NO_WRAP_STYLE = { whiteSpace: 'nowrap' };

const FUNDING_TYPE_OPTIONS = [
  {
    value: '9_PERCENT_ALLOCATED_FEDERAL_HOUSING_TAX_CREDIT',
    text: '9% Allocated Federal Housing Tax Credit',
  },
  {
    value: '4_PERCENT_ALLOCATED_FEDERAL_HOUSING_TAX_CREDIT',
    text: '4% Allocated Federal Housing Tax Credit',
  },
  {
    value: 'TAX_EXEMPT_BOND_ONLY',
    text: 'Tax-Exempt Bond Only (No Tax Credits)',
  },
];

const getConstructionTypeOptions = (intl: Object) => [
  { value: '', text: intl.formatMessage(appMessages.choose) },
  { value: 'REHAB', text: intl.formatMessage(messages.rehab) },
  {
    value: 'NEW_CONSTRUCTION',
    text: intl.formatMessage(messages.newConstruction),
  },
];

export const Row = ({
  building,
  updateBuilding,
  setSelectedBuilding,
  disabled,
  states,
  selectedProperty,
  promptToaster,
  intl,
  showFundingType,
}: Props): FunctionComponent<Props> => {
  const { publicHousing, naur, bldgCertoccConstypeFields } = useFlags();
  const alphaNumericRegex = /^[a-zA-Z0-9]*$/;
  const onPHAlphaNumericInputChange = (
    e: ChangeEvent<HTMLInputElement>,
    setValue: Function,
  ) => {
    const { value } = e.target;
    if (alphaNumericRegex.test(value)) {
      setValue(value);
    } else {
      e.preventDefault();
    }
  };

  const onApplicableFractionChange = (
    e: ChangeEvent<HTMLInputElement>,
    setValue: Function,
  ) => {
    const value = e.target.value;
    if (value === '') {
      setValue(null);
    } else {
      const numValue = parseFloat(value);
      // Only update if valid and within range (0 to 100)
      if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
        // Reject more than 2 decimal places
        const decimalIndex = value.indexOf('.');
        if (decimalIndex !== -1 && value.length - decimalIndex - 1 > 2) {
          return;
        }
        setValue(value);
      }
    }
  };

  const [editMode, setEditMode] = useState(false);
  const [streetAddress1, setStreetAddress1] = useState(
    building?.streetAddress1,
  );
  const [city, setCity] = useState(building?.city);
  const [state, setState] = useState(building?.state);
  const [zipCode, setZipCode] = useState(building?.zipCode);
  const [buildingIDNumber, setBuildingIdNumber] = useState(
    building?.buildingIDNumber ?? '',
  );
  const [RDSiteId, setRDSiteId] = useState(building?.RDSiteId ?? '');
  const [XMLSiteName, setXMLSiteName] = useState(building?.XMLSiteName ?? '');
  const [placedInServiceDate, setPlacedInServiceDate] = useState(
    building?.placedInServiceDate ?? null,
  );
  const [heraSpecial, setHeraSpecial] = useState(
    building?.heraSpecial ?? false,
  );
  const [minimumSetAside, setMinimumSetAside] = useState(
    building?.minimumSetAside ?? '',
  );
  const [PHBuildingNumber, setPHBuildingNumber] = useState(
    building?.PHBuildingNumber ?? '',
  );
  const [PHBuildingEntrance, setPHBuildingEntrance] = useState(
    building?.PHBuildingEntrance ?? '',
  );
  const [fundingType, setFundingType] = useState(building?.fundingType ?? null);
  const [applicableFractionUnit, setApplicableFractionUnit] = useState(
    building?.applicableFractionUnit ?? null,
  );
  const [applicableFractionFloorSpace, setApplicableFractionFloorSpace] =
    useState(building?.applicableFractionFloorSpace ?? null);
  const [certificateOfOccupancyDate, setCertificateOfOccupancyDate] = useState(
    building?.certificateOfOccupancyDate ?? null,
  );
  const [constructionType, setConstructionType] = useState(
    building?.constructionType ?? '',
  );

  const isAffordableProperty = useIsAffordableProperty(selectedProperty);
  const isRDProperty = usePropertyHasProgram(
    selectedProperty,
    AFFORDABLE_PROGRAMS.RD,
  );
  const isLIHTCProperty = usePropertyHasProgram(
    selectedProperty,
    AFFORDABLE_PROGRAMS.LIHTC,
  );
  const isPHProperty =
    usePropertyHasProgram(selectedProperty, AFFORDABLE_PROGRAMS.PH) &&
    publicHousing;
  const showCertOccConstructionType =
    bldgCertoccConstypeFields && isAffordableProperty && isLIHTCProperty;
  const constructionTypeOptions = getConstructionTypeOptions(intl);

  const queryClient = useQueryClient();
  const {
    data: naurEventsData,
    isLoading: isNaurLoading,
    isError: isNaurError,
    refetch: refetchNaurEvents,
  } = useNaurEventsForBuilding(building.id, Boolean(naur && building.id));

  const hasUnresolvedNaurs = useMemo(() => {
    if (!naur) return false;
    if (isNaurLoading || isNaurError) return true; // fail safe
    if (!naurEventsData?.naurEvents?.length) return false;
    return naurEventsData.naurEvents.some((e) => e.status !== 'SATISFIED');
  }, [naur, isNaurLoading, isNaurError, naurEventsData]);

  const disabledFractionTooltipTitle = useMemo(() => {
    if (!naur || !hasUnresolvedNaurs) return null;
    if (isNaurError)
      return intl.formatMessage(messages.errorNaurStatusLoadFailed);
    return intl.formatMessage(messages.disabledApplicableFractionTooltip);
  }, [naur, hasUnresolvedNaurs, isNaurError, intl]);

  useEffect(() => {
    setStreetAddress1(building?.streetAddress1);
    setCity(building?.city);
    setState(building?.state);
    setZipCode(building?.zipCode);
    setBuildingIdNumber(building?.buildingIDNumber ?? '');
    setPlacedInServiceDate(building?.placedInServiceDate ?? null);
    setApplicableFractionUnit(building?.applicableFractionUnit ?? null);
    setApplicableFractionFloorSpace(
      building?.applicableFractionFloorSpace ?? null,
    );
    setCertificateOfOccupancyDate(building?.certificateOfOccupancyDate ?? null);
    setConstructionType(building?.constructionType ?? '');
  }, [building]);

  const isFundingTypeValid = () => {
    if (showFundingType && !fundingType) {
      promptToaster({
        type: 'error',
        title: intl.formatMessage(appMessages.error),
        message: intl.formatMessage(messages.errorFundingTypeRequired),
      });
      return false;
    }

    return true;
  };

  const isApplicableFractionValid = () => {
    const isUnitValid = validateApplicableFractionValue(applicableFractionUnit);
    const isFloorSpaceValid = validateApplicableFractionValue(
      applicableFractionFloorSpace,
    );

    if (!isUnitValid || !isFloorSpaceValid) {
      promptToaster({
        type: 'error',
        title: intl.formatMessage(appMessages.error),
        message: intl.formatMessage(messages.errorApplicableFractionInvalid),
      });
      return false;
    }

    return true;
  };

  const onEdit = () => {
    if (editMode) {
      if (!isFundingTypeValid()) {
        return;
      }

      if (!isApplicableFractionValid()) {
        return;
      }

      const newUnit =
        applicableFractionUnit !== null && applicableFractionUnit !== ''
          ? Number(applicableFractionUnit)
          : null;
      const newFloorSpace =
        applicableFractionFloorSpace !== null &&
        applicableFractionFloorSpace !== ''
          ? Number(applicableFractionFloorSpace)
          : null;

      const fractionsChanged =
        naur &&
        (newUnit !== (building.applicableFractionUnit ?? null) ||
          newFloorSpace !== (building.applicableFractionFloorSpace ?? null));

      // Guard: only block the save when fractions actually changed AND unresolved
      // NAURs exist. Saves for other fields (address, BIN, etc.) must still go through.
      if (fractionsChanged && hasUnresolvedNaurs) {
        promptToaster({
          type: 'error',
          title: intl.formatMessage(appMessages.error),
          message: intl.formatMessage(messages.errorUnresolvedNaursOnSave),
        });
        return;
      }

      const rdData = isRDProperty
        ? {
            RDSiteId: RDSiteId.trim(),
          }
        : {};

      const publicHousingData =
        publicHousing && isPHProperty
          ? {
              PHBuildingNumber: PHBuildingNumber?.trim() ?? '',
              PHBuildingEntrance: PHBuildingEntrance?.trim() ?? '',
            }
          : {};

      const naurData = naur
        ? {
            applicableFractionUnit: newUnit,
            applicableFractionFloorSpace: newFloorSpace,
          }
        : {};

      const certOccConstructionTypeData = showCertOccConstructionType
        ? {
            // Date-only column: keep the picked local day, no UTC shift
            certificateOfOccupancyDate:
              formatDateDB(certificateOfOccupancyDate, false) || null,
            constructionType: constructionType || null,
          }
        : {};

      const data = {
        streetAddress1: streetAddress1.trim(),
        city: city.trim(),
        state,
        zipCode,
        buildingIDNumber: buildingIDNumber.trim(),
        placedInServiceDate: placedInServiceDate || null,
        XMLSiteName: XMLSiteName ? XMLSiteName.trim() : null,
        heraSpecial: heraSpecial,
        fundingType: showFundingType ? fundingType : null,
        minimumSetAside,
        ...rdData,
        ...publicHousingData,
        ...naurData,
        ...certOccConstructionTypeData,
      };

      updateBuilding(selectedProperty, building.id, data, promptToaster, intl, {
        onSuccess: () => {
          if (fractionsChanged) {
            queryClient.invalidateQueries([
              'naur-property-compliance-details',
              selectedProperty.id,
            ]);
            queryClient.invalidateQueries([
              'naurEvents',
              'building',
              building.id,
            ]);
          }
        },
        onNaurError: () => {
          promptToaster({
            type: 'error',
            title: intl.formatMessage(appMessages.error),
            message: intl.formatMessage(
              messages.errorApplicableFractionNaurApi,
            ),
          });
          refetchNaurEvents();
        },
      });

      setSelectedBuilding(null);
    } else {
      setSelectedBuilding(building.id);
    }
    setEditMode(!editMode);
  };

  const address = `${streetAddress1}, ${city}, ${
    state.split('-')[1]
  } ${zipCode}`;

  return (
    <tr>
      <td className={`col-md-${isAffordableProperty ? '1' : '2'}`}>
        {building.name}
      </td>
      <td
        className={`col-md-${isRDProperty && isAffordableProperty ? '3' : '4'}`}
        style={NO_WRAP_STYLE}
      >
        {editMode ? (
          <Stack direction={'row'} sx={{ minWidth: 520 }}>
            <AddressInput
              maxWidth="34%"
              value={streetAddress1}
              setValue={setStreetAddress1}
            />
            <AddressInput maxWidth="30%" value={city} setValue={setCity} />
            <StateSelect
              states={states}
              value={state}
              onChange={(e) => setState(e.target.value)}
              maxWidth="21%"
            />
            <AddressInput
              maxWidth="15%"
              value={zipCode}
              setValue={setZipCode}
              onChange={(e) => setZipCode(e.target.value)}
            />
          </Stack>
        ) : (
          address
        )}
      </td>
      {isAffordableProperty && (
        <td className="col-md-1">
          {editMode ? (
            <Input
              maxWidth="80%"
              margin="auto"
              value={XMLSiteName}
              setValue={setXMLSiteName}
              maxLength={255}
            />
          ) : (
            XMLSiteName || '---'
          )}
        </td>
      )}

      <td className="col-md-1">
        {editMode ? (
          <CalendarDatePicker
            value={placedInServiceDate}
            onChange={setPlacedInServiceDate}
          />
        ) : placedInServiceDate ? (
          moment.utc(placedInServiceDate).format('MM/DD/YYYY')
        ) : (
          '---'
        )}
      </td>
      <td
        className={`col-md-${isAffordableProperty && isRDProperty ? '1' : '2'}`}
        style={NO_WRAP_STYLE}
      >
        {editMode ? (
          <Input
            maxWidth="80%"
            margin="auto"
            value={buildingIDNumber}
            setValue={setBuildingIdNumber}
          />
        ) : (
          buildingIDNumber || '---'
        )}
      </td>
      {isPHProperty && isAffordableProperty && (
        <>
          <td className="col-md-1">
            {editMode ? (
              <Input
                maxWidth="80%"
                margin="auto"
                value={PHBuildingNumber}
                onChange={(e) =>
                  onPHAlphaNumericInputChange(e, setPHBuildingNumber)
                }
                maxLength={6}
              />
            ) : (
              PHBuildingNumber || '---'
            )}
          </td>
          <td className="col-md-1">
            {editMode ? (
              <Input
                maxWidth="80%"
                margin="auto"
                value={PHBuildingEntrance}
                onChange={(e) =>
                  onPHAlphaNumericInputChange(e, setPHBuildingEntrance)
                }
                maxLength={3}
              />
            ) : (
              PHBuildingEntrance || '---'
            )}
          </td>
        </>
      )}
      {isRDProperty && isAffordableProperty && (
        <td className="col-md-2">
          {editMode ? (
            <Input
              maxWidth="80%"
              margin="auto"
              value={RDSiteId}
              setValue={setRDSiteId}
            />
          ) : (
            RDSiteId || '---'
          )}
        </td>
      )}
      {isAffordableProperty && isLIHTCProperty && (
        <td className="col-md-1">
          {editMode ? (
            <MinimumSetAside
              name="minimumSetAside"
              value={minimumSetAside}
              onChange={(value: string) => {
                setMinimumSetAside(value);
              }}
              city={city}
              state={state}
            />
          ) : (
            minimumSetAside || '---'
          )}
        </td>
      )}
      {isAffordableProperty && isLIHTCProperty && naur && (
        <td className="col-md-1">
          {editMode ? (
            <Tooltip title={disabledFractionTooltipTitle} placement="top">
              {/* span wrapper required so Tooltip fires on disabled inputs */}
              <span style={{ display: 'block' }}>
                <Input
                  maxWidth="80%"
                  margin="auto"
                  value={applicableFractionUnit ?? ''}
                  onChange={(e) =>
                    onApplicableFractionChange(e, setApplicableFractionUnit)
                  }
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  disabled={hasUnresolvedNaurs}
                />
              </span>
            </Tooltip>
          ) : (
            formatNaurPercentage(applicableFractionUnit)
          )}
        </td>
      )}
      {isAffordableProperty && isLIHTCProperty && naur && (
        <td className="col-md-1">
          {editMode ? (
            <Tooltip title={disabledFractionTooltipTitle} placement="top">
              {/* span wrapper required so Tooltip fires on disabled inputs */}
              <span style={{ display: 'block' }}>
                <Input
                  maxWidth="80%"
                  margin="auto"
                  value={applicableFractionFloorSpace ?? ''}
                  onChange={(e) =>
                    onApplicableFractionChange(
                      e,
                      setApplicableFractionFloorSpace,
                    )
                  }
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  disabled={hasUnresolvedNaurs}
                />
              </span>
            </Tooltip>
          ) : (
            formatNaurPercentage(applicableFractionFloorSpace)
          )}
        </td>
      )}
      {isAffordableProperty && (
        <td className="col-md-1">
          {editMode ? (
            <StyledSwitch
              name="heraSpecial"
              size="small"
              edge="start"
              onLabel={intl.formatMessage(appMessages.yes)}
              offLabel={intl.formatMessage(appMessages.no)}
              onChange={(
                evt: ChangeEvent<HTMLInputElement>,
                checked: boolean,
              ) => {
                setHeraSpecial(checked);
              }}
              checked={heraSpecial}
            />
          ) : (
            intl.formatMessage(appMessages?.[heraSpecial ? 'yes' : 'no'])
          )}
        </td>
      )}
      {showFundingType && (
        <td className="col-md-1" style={NO_WRAP_STYLE}>
          {editMode ? (
            <Select
              name="fundingType"
              fullWidth
              value={fundingType}
              options={FUNDING_TYPE_OPTIONS}
              onChange={(evt: ChangeEvent<HTMLInputElement>) =>
                setFundingType(evt.target.value)
              }
            />
          ) : (
            FUNDING_TYPE_OPTIONS.find(({ value }) => value === fundingType)
              ?.text || '---'
          )}
        </td>
      )}
      {showCertOccConstructionType && (
        <>
          <td className="col-md-1">
            {editMode ? (
              <CalendarDatePicker
                value={certificateOfOccupancyDate}
                onChange={setCertificateOfOccupancyDate}
              />
            ) : (
              formatCalendarDateUtc(certificateOfOccupancyDate) ?? '---'
            )}
          </td>
          <td className="col-md-1">
            {editMode ? (
              <Select
                name="constructionType"
                fullWidth
                displayEmpty
                value={constructionType}
                options={constructionTypeOptions}
                onChange={(evt: ChangeEvent<HTMLInputElement>) =>
                  setConstructionType(evt.target.value)
                }
              />
            ) : (
              (constructionType &&
                constructionTypeOptions.find(
                  ({ value }) => value === constructionType,
                )?.text) ||
              '---'
            )}
          </td>
        </>
      )}
      <td className="col-md-2">
        <ElementWithPermissions scope={['floor-plan-update']}>
          <button
            type="button"
            className="btn btn-sm btn-default"
            onClick={onEdit}
            disabled={disabled}
          >
            <i className={`et-${editMode ? 'floppy' : 'pencil'}`}></i>
          </button>
        </ElementWithPermissions>
      </td>
    </tr>
  );
};
